const PlayerState = require('../entities/PlayerState');
const CardConverter = require('../Utilities/Converters/CardConverter');
const CardToData = require('../Utilities/Converters/CardToData');
const LeaderNames = require('../Constants/Names/LeaderNames');
const { Phase } = require('../Moves/MoveTypes');
const { Age, AgeOrder } = require('../Constants/Constants');

class GameState {
    constructor(gameStateData)
    {
        // Turn info first: player states read the round when computing available actions
        this.playerCount = gameStateData.playerCount ?? gameStateData.playerStateData.length;
        this.currentPlayer = gameStateData.currentPlayer ?? 0;
        this.phase = gameStateData.phase ?? Phase.Political;
        this.round = gameStateData.round ?? 1;
        // What cards did in reaction to the move being applied, for its log line (not saved; see Effects.FireTriggers)
        this.triggerLog = [];
        // Why cards did nothing in reaction, for the end-of-turn preview (not saved)
        this.triggerNotes = [];
        this.age = gameStateData.age ?? Age.AgeA;
        this.nextCardId = gameStateData.nextCardId;
        this.rngSeed = gameStateData.rngSeed ?? 1;
        // What the most recently revealed current event did, for the journal and the board
        this.lastEvent = gameStateData.lastEvent ?? null;
        // Decisions players still have to make (see Moves/Decisions.js), oldest first
        this.decisions = (gameStateData.decisions ?? []).map(decision => JSON.parse(JSON.stringify(decision)));
        this.nextDecisionId = gameStateData.nextDecisionId ?? 1;
        // A territory being auctioned (see Moves/Colonization.js): { territory, order, passed, highest }, or null
        this.auction = gameStateData.auction ? JSON.parse(JSON.stringify(gameStateData.auction)) : null;
        // An aggression under way (see Moves/Aggression.js), or null
        this.aggression = gameStateData.aggression ? JSON.parse(JSON.stringify(gameStateData.aggression)) : null;
        // Wars declared and not yet resolved (see Moves/War.js)
        this.wars = (gameStateData.wars ?? []).map(war => JSON.parse(JSON.stringify(war)));
        // Pacts in play and the offer waiting for an answer (see Moves/Pact.js)
        this.pacts = (gameStateData.pacts ?? []).map(pact => JSON.parse(JSON.stringify(pact)));
        this.pactOffer = gameStateData.pactOffer ? JSON.parse(JSON.stringify(gameStateData.pactOffer)) : null;
        // The political action just played is still being resolved (its blocking decisions are open): the turn is
        // in the action phase as far as the rules go, but it is shown as still the political phase
        this.politicalResolving = gameStateData.politicalResolving ?? false;
        // The last round of the game, once it is known (null until then)
        this.finalRound = gameStateData.finalRound ?? null;
        // The finished game: standings, winners and final scoring (see Moves/GameEnd.js), or null
        this.finished = gameStateData.finished ? JSON.parse(JSON.stringify(gameStateData.finished)) : null;
        // What happened in the move worth everyone's "since your last turn" (e.g. how an aggression turned out; not saved)
        this.reports = [];
        // Military cards shuffled back from the discard into the deck in the move (card ids; not saved): no one knows
        // where they are any more
        this.reshuffled = [];
        // What in the move can leave a player owing blue tokens (an event, an aggression, a pact ending), for the
        // decision that settles it (not saved)
        this.blueCause = null;
        // A player who resigned in their political phase: the turn passes on once what it set off is decided
        // (see Moves/GameEnd.js Resign)
        this.resignAdvance = gameStateData.resignAdvance ?? null;
        // Military cards discarded face down; they will be reshuffled when the military deck runs out
        this.militaryDiscard = (gameStateData.militaryDiscard ?? []).map(cardData => CardConverter(cardData));
        // Who prepared each event in the current and future events decks: card id -> player number
        this.eventOwners = { ...(gameStateData.eventOwners ?? {}) };
        // The same, as a player's view gives it (ids there are placeholders): { current: [{ player, age, name? }], future }
        this.eventsPrepared = gameStateData.eventsPrepared ?? null;

        // The decks, leaders and wonders the game was set up with, and the versions of changed cards (see
        // Constants/CardVersions.js; none: a game from before there was a choice). Known before the players are built:
        // their technologies are in its versions.
        this.setup = gameStateData.setup ?? null;
        this.playerStates = gameStateData.playerStateData.map( p => new PlayerState(p,this))
        // Wars in play change both sides until resolved (Hybrid War's happy faces)
        require('../Moves/War').ApplyLastingEffects(this);
        // and pacts both sides until they end
        require('../Moves/Pact').ApplyPactEffects(this);
        // A revolution this move spent every action: the ones the new government adds are spent too (any given back
        // since, e.g. by Isaac Newton, stay given back)
        this.playerStates.forEach(player => player.turnFlags.filter(flag => flag.startsWith('revolution:')).forEach(flag => {
            const [, kind, before] = flag.split(':');
            const now = kind === 'civil' ? player.GetCivilActionCount() : player.GetMilitaryActionCount();
            const added = Math.max(0, now - Number(before));
            if (kind === 'civil') player.civilActionsUsed += added; else player.militaryActionsUsed += added;
            player.turnFlags = player.turnFlags.filter(other => other !== flag);
        }));
        this.civilDeck = gameStateData.civilDeck.map(cardData => CardConverter(cardData));
        this.militaryDeck = gameStateData.militaryDeck.map(cardData => CardConverter(cardData));
        this.futureEvents = gameStateData.futureEvents.map(cardData => CardConverter(cardData));
        this.currentEvents = gameStateData.currentEvents.map(cardData => CardConverter(cardData));
        // Age A events not used this game, in the box (Development of Planning draws from them)
        this.eventBox = (gameStateData.eventBox ?? []).map(cardData => CardConverter(cardData));
        this.civilCardRow = gameStateData.civilCardRow.map(cardData => CardConverter(cardData));
        // Tactics anyone can copy
        this.publicTactics = (gameStateData.publicTactics ?? []).map(cardData => CardConverter(cardData));
        // Strength per army a tactic card has gained, by card id (Sun Tzu went under it)
        this.tacticBonuses = gameStateData.tacticBonuses ?? {};
        // Alfred Nobel is by the culture track (replaced): the Nobel Prize is in effect for everyone
        this.nobelPrize = gameStateData.nobelPrize ?? false;
        // Pierre de Coubertin's Olympic Games, until the start of his player's next turn: { player } (whoever plays a war or
        // aggression meanwhile loses 8 culture), or null
        this.olympics = gameStateData.olympics ?? null;
        // Players whose blue tokens owed (an empty bank) are settled only at the end of the current turn: another player's
        // move caused it (a stage of their International Red Cross), so the choice waits (player numbers)
        this.deferredBlue = [...(gameStateData.deferredBlue ?? [])];
        // Decks for the ages still to come, in order: { age, civil, military }
        this.upcomingDecks = (gameStateData.upcomingDecks ?? []).map(deck => ({
            age: deck.age,
            civil: deck.civil.map(cardData => CardConverter(cardData)),
            military: deck.military.map(cardData => CardConverter(cardData)),
        }));
    }

    // Serializable form; round-trips through the constructor
    toData() {
        return {
            playerCount: this.playerCount,
            currentPlayer: this.currentPlayer,
            phase: this.phase,
            round: this.round,
            age: this.age,
            nextCardId: this.nextCardId,
            rngSeed: this.rngSeed,
            lastEvent: this.lastEvent,
            decisions: this.decisions.map(decision => JSON.parse(JSON.stringify(decision))),
            nextDecisionId: this.nextDecisionId,
            auction: this.auction ? JSON.parse(JSON.stringify(this.auction)) : null,
            aggression: this.aggression ? JSON.parse(JSON.stringify(this.aggression)) : null,
            wars: this.wars.map(war => JSON.parse(JSON.stringify(war))),
            pacts: this.pacts.map(pact => JSON.parse(JSON.stringify(pact))),
            pactOffer: this.pactOffer ? JSON.parse(JSON.stringify(this.pactOffer)) : null,
            finalRound: this.finalRound,
            politicalResolving: this.politicalResolving,
            resignAdvance: this.resignAdvance,
            finished: this.finished ? JSON.parse(JSON.stringify(this.finished)) : null,
            militaryDiscard: this.militaryDiscard.map(CardToData),
            eventOwners: { ...this.eventOwners },
            ...(this.eventsPrepared ? { eventsPrepared: this.eventsPrepared } : {}),
            playerStateData: this.playerStates.map(p => p.toData()),
            civilDeck: this.civilDeck.map(CardToData),
            militaryDeck: this.militaryDeck.map(CardToData),
            futureEvents: this.futureEvents.map(CardToData),
            currentEvents: this.currentEvents.map(CardToData),
            eventBox: this.eventBox.map(CardToData),
            civilCardRow: this.civilCardRow.map(CardToData),
            publicTactics: this.publicTactics.map(CardToData),
            tacticBonuses: this.tacticBonuses,
            nobelPrize: this.nobelPrize,
            olympics: this.olympics,
            deferredBlue: [...this.deferredBlue],
            ...(this.setup ? { setup: JSON.parse(JSON.stringify(this.setup)) } : {}),
            upcomingDecks: this.upcomingDecks.map(deck => ({
                age: deck.age,
                civil: deck.civil.map(CardToData),
                military: deck.military.map(CardToData),
            })),
        };
    }

    GetCurrentPlayerState = () => this.playerStates[this.currentPlayer];

    // The events each player has prepared that are still face down: { current: [{ player, age, name }], future: [...] }
    // (name only where the one looking knows it)
    GetEventsPrepared() {
        if (this.eventsPrepared)
        {
            return this.eventsPrepared;
        }
        const prepared = (cards) => cards
            .filter(card => this.eventOwners[card.id] !== undefined)
            .map(card => ({ player: this.eventOwners[card.id], age: card.age, type: card.type, name: card.name }));
        return { current: prepared(this.currentEvents), future: prepared(this.futureEvents) };
    }

    // Deterministic random number in [0, 1) from the game's seed (mulberry32), so replaying moves gives the same result
    Random() {
        this.rngSeed = (this.rngSeed + 0x6D2B79F5) | 0;
        let t = this.rngSeed;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    }

    // The player draws military cards, as many as there are; an empty military deck is made again from the shuffled
    // discard (not in Age IV, which has no military deck). Returns how many were drawn.
    DrawMilitaryCards(player, count) {
        let drawn = 0;
        // (no military cards are drawn in Age IV)
        while (drawn < count && this.age !== Age.Age4)
        {
            if (this.militaryDeck.length === 0)
            {
                if (this.militaryDiscard.length === 0)
                {
                    break;
                }
                this.militaryDeck = this.Shuffle(this.militaryDiscard);
                this.militaryDiscard = [];
                this.reshuffled.push(...this.militaryDeck.map(card => card.id));
                this.reports.push({ kind: 'notice', name: 'Military deck', round: this.round, changes: [],
                    text: `The military deck ran out: the discard (${this.militaryDeck.length} cards) is shuffled into a new one.` });
            }
            player.militaryHand.push(this.militaryDeck.shift());
            drawn++;
        }
        return drawn;
    }

    Shuffle(cards) {
        for (let i = cards.length - 1; i > 0; i--)
        {
            const j = Math.floor(this.Random() * (i + 1));
            [cards[i], cards[j]] = [cards[j], cards[i]];
        }
        return cards;
    }

    // The next age begins: its decks replace the current ones. Returns false when there are no ages left.
    // From Age II on, the cards of two ages back become obsolete (see RetireAge) and every civilization loses
    // 2 yellow tokens. What happens is noted in the move log.
    // A new age, as its own entry in the journal and everyone's recap (it happens with the card row's refill at the start
    // of a turn, not as part of the turn before). revealed: the age's leaders and wonders, when a mix reveals them now
    // ({ leaders, wonders }: names, shown as cards)
    NoteNewAge(text, revealed = null) {
        this.reports.push({ kind: 'notice', name: text.split(':')[0], round: this.round, changes: [], journal: 'Game', text,
            newAge: true, age: this.age, ...(revealed ? { revealed } : {}) });
    }

    StartNextAge() {
        const next = this.upcomingDecks.shift();
        if (next === undefined)
        {
            // Age III's civil deck has run out: Age IV begins (Age II cards become obsolete), with no new cards
            if (this.age === Age.Age3)
            {
                this.age = Age.Age4;
                // There is no military deck in Age IV: what is left of Age III's leaves the game, and nothing can be drawn
                this.militaryDeck = [];
                this.militaryDiscard = [];
                const notes = this.AgeLossNotes(this.playerStates.map(player => this.RetireAge(player, Age.Age2, Age.Age4)));
                const pacts = require('../Moves/Pact').RetirePacts(this, Age.Age2);
                this.NoteNewAge(`${Age.Age4} begins: ${[...notes, ...(pacts.length ? [`pacts discarded: ${pacts.join(', ')}`] : [])].join('; ')}`);
                // and with it the end of the game
                require('../Moves/GameEnd').SetFinalRound(this);
            }
            return false;
        }
        this.age = next.age;
        this.civilDeck = next.civil;
        // The old age's military cards leave the game: what is left of its deck and its discard
        this.militaryDeck = next.military;
        this.militaryDiscard = [];
        const level = AgeOrder.indexOf(next.age);
        // What cards do as it begins (the expansion's Colossus draws), in the age's note
        const started = this.playerStates.filter(player => !player.resigned).flatMap(player => player.triggerDelegate
            .filter(trigger => trigger.event === 'ageStart')
            .map(trigger => { const text = trigger.Action({ age: next.age }); return text ? `${player.name}'s ${trigger.Source?.name}: ${text}` : null; })
            .filter(text => text));
        const notes = [];
        if (level >= 2)
        {
            notes.push(...this.AgeLossNotes(this.playerStates.map(player => this.RetireAge(player, AgeOrder[level - 2], next.age))), ...started);
            // Pacts two ages old are discarded too
            const pacts = require('../Moves/Pact').RetirePacts(this, AgeOrder[level - 2]);
            if (pacts.length)
            {
                notes.push(`pacts discarded: ${pacts.join(', ')}`);
            }
        }
        // A mix revealed age by age: the age's leaders and wonders are shown as it begins
        const picked = this.setup?.cards === 'mix' && this.setup.reveal === 'byAge' ? this.setup.selection?.[next.age] : null;
        // (named as cards, apart from the text)
        this.NoteNewAge(`${next.age} begins${notes.length ? `: ${notes.join('; ')}` : ''}`,
            picked ? { leaders: picked.leaders, wonders: picked.wonders } : null);
        return true;
    }

    // A civilization at the start of a new age: its leader, unfinished wonder and civil cards in hand from the obsolete
    // age are discarded, and it loses 2 yellow tokens from its yellow bank (only as many as are there: never workers).
    // Returns what it lost: { player, cards (named as the cards: CardRefs, hover for the card, a military card face down),
    // tokens (yellow tokens lost, net of Hippocrates' one back), hippocrates (whether one came back) }
    RetireAge(player, obsoleteAge, newAge) {
        const { CardRef, OwnedRef } = require('../Utilities/CardRefs');
        const lost = [];
        if (player.leader?.age === obsoleteAge)
        {
            lost.push(CardRef(player.leader));
            // Leaving play (e.g. Confucius scores, Sun Tzu goes under the tactic)
            require('../Effects/Effects').FireTriggers(player, require('../Effects/Effects').TriggerEvents.LeaderLeaves, { leader: player.leader });
            player.leader = null;
        }
        if (player.wonderInProgress?.card.age === obsoleteAge)
        {
            lost.push(`unfinished ${CardRef(player.wonderInProgress.card)}`);
            player.wonderInProgress = null;
        }
        const obsoleteCards = player.civilHand.filter(card => card.age === obsoleteAge);
        if (obsoleteCards.length > 0)
        {
            lost.push(obsoleteCards.map(CardRef).join(' '));
            player.civilHand = player.civilHand.filter(card => card.age !== obsoleteAge);
        }
        // Military cards in hand from that age too (face down: hidden from everyone else, but their owner sees which);
        // they leave the game with their age's deck
        const obsoleteMilitary = player.militaryHand.filter(card => card?.age === obsoleteAge);
        if (obsoleteMilitary.length > 0)
        {
            lost.push(obsoleteMilitary.map(card => OwnedRef(card, player.playerNumber)).join(' '));
            player.militaryHand = player.militaryHand.filter(card => card?.age !== obsoleteAge);
        }
        let tokens = Math.min(2, Math.max(0, player.yellowBank));
        if (tokens > 0)
        {
            player.ChangeTokens('yellow', -tokens, `Start of ${newAge}`);
        }
        // Hippocrates' tokens: one of them goes into the yellow bank after the loss
        const hippocrates = player.CardState(LeaderNames.Hippocrates);
        const back = hippocrates.tokens > 0;
        if (back)
        {
            hippocrates.tokens--;
            player.ChangeTokens('yellow', 1, 'Hippocrates');
            tokens--;
        }
        return { player: player.name, cards: lost, tokens, hippocrates: back };
    }

    // The new age's note, a line each: "all players lose 2 yellow tokens" when that is so for everyone, then the cards each
    // lost; otherwise each civilization's cards and tokens ("Dave loses [Hippocrates], 1 yellow token ([Hippocrates])": one came
    // back from Hippocrates)
    AgeLossNotes(losses) {
        const { CardRef } = require('../Utilities/CardRefs');
        const hippocrates = CardRef({ type: 'Leader', name: LeaderNames.Hippocrates, age: Age.AgeA });
        const tokens = (count) => `${count} yellow token${count === 1 ? '' : 's'}`;
        if (losses.every(loss => loss.tokens === 2 && !loss.hippocrates))
        {
            return [`all players lose ${tokens(2)}`, ...losses.filter(loss => loss.cards.length > 0).map(loss => `${loss.player} loses ${loss.cards.join(', ')}`)];
        }
        return losses.map(loss => {
            const parts = [...loss.cards];
            if (loss.tokens > 0 || loss.hippocrates)
            {
                parts.push(`${loss.tokens > 0 ? tokens(loss.tokens) : 'no yellow tokens'}${loss.hippocrates ? ` (${hippocrates})` : ''}`);
            }
            return parts.length > 0 ? `${loss.player} loses ${parts.join(', ')}` : null;
        }).filter(note => note);
    }

}

module.exports = GameState;
